import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, X, ArrowRight } from "lucide-react";

// Mounted once in App.js. Any component opens it with
// openPremiumRequiredPopup() instead of alert("Please Buy Premium").
const listeners = new Set();

export const openPremiumRequiredPopup = (options = {}) => {
  listeners.forEach((listener) => listener(options));
};

const DEFAULT_BENEFITS = [
  "Owner & broker property listings",
  "Advanced search and filters",
  "Direct contact details",
];

const PremiumRequiredPopup = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const primaryButtonRef = useRef(null);

  useEffect(() => {
    const listener = (next) => setOptions(next || {});
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const isOpen = Boolean(options);

  useEffect(() => {
    if (!isOpen) return undefined;

    primaryButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOptions(null);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const close = () => setOptions(null);
  const handleViewPlans = () => {
    setOptions(null);
    navigate("/subscription");
  };

  const benefits = Array.isArray(options.benefits) && options.benefits.length > 0 ? options.benefits : DEFAULT_BENEFITS;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in-0 duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="premium-required-title"
      aria-describedby="premium-required-message"
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-300 motion-reduce:animate-none">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#503691] via-purple-600 to-amber-400" />

        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900"
        >
          <X size={18} />
        </button>

        <div className="px-6 pb-6 pt-8 sm:px-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-white shadow-lg shadow-amber-200">
            <Crown size={30} strokeWidth={2.2} />
          </div>

          <h2 id="premium-required-title" className="mt-5 text-center text-2xl font-bold text-gray-900">
            {options.title || "Unlock with Premium"}
          </h2>
          <p id="premium-required-message" className="mt-2 text-center text-sm leading-relaxed text-gray-600">
            {options.message || "This feature is available on Premium plans. Upgrade to get full access."}
          </p>

          <ul className="mt-5 space-y-2.5 rounded-xl border border-violet-100 bg-violet-50/60 p-4">
            {benefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-2.5 text-sm font-medium text-gray-700">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#503691] text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                {benefit}
              </li>
            ))}
          </ul>

          <button
            ref={primaryButtonRef}
            type="button"
            onClick={handleViewPlans}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#503691] to-purple-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-[#503691]/25 transition-all hover:from-[#3f2d7a] hover:to-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503691] focus-visible:ring-offset-2"
          >
            View Premium Plans
            <ArrowRight size={18} />
          </button>
          <button
            type="button"
            onClick={close}
            className="mt-2 w-full rounded-xl py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-800"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default PremiumRequiredPopup;
